import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { appRef } from '../../../app'
import { HiSwitchVertical, HiHashtag, HiChip, HiOutlineMenu } from 'react-icons/hi'
import classnames from 'classnames'
import { onCustomerConnect, clearCustomerConnect, IListenerUID, CustomerState } from '../../../wsclient/handlers/customertraffic.handler'
import { IClientData, getClientById } from '../../../api/clients'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import { ChipModel } from '../../../constants/chip-model.enum'
import { IStaticTraits, getClientStaticTraits } from '../../../api/0x0005/statictraits'

import { getMachine, IMachine } from '../../../api/0x0002/machine'
import { isSoftwareOutdated } from '../../../api/0x0002/software-versions'
import CustomerDataTab from './customerdata.tab'
import OtaTab from '../0x0001/ota.tab'
import FlashDeviceTab from '../0x0001/flashdevice.tab'
import ChannelsTab from './channels.tab'
import DrinksTab from './drinks.tab'
import SyrupsTab from './syrups.tab'
import SyrupUsageTab from './syrup-usage.tab'
import PumpUsageTab from './pump-usage.tab'
import PouringTab from './pouring.tab'
import SystemTab from './system.tab'
import GeneralSettingsTab from './general-settings.tab'
import BluetoothTab from './bluetooth.tab'
import WifiTab from './wifi.tab'
import CatalogTab from './catalog.tab'
import AutoRinseTab from './auto-rinse.tab'
import PublicTextsTab from './public-texts.tab'
import UserSecurityTab from './user-security.tab'
import LicenseTab from './license.tab'
import SanitationTab from './sanitation.tab'
import LiveLogsTab from './live-logs.tab'
import { getRemoteLiveLogStream } from '../../../api/0x0005/general-settings'

type Props = {
    client: IClientData
}

type State = {
    showMobileMenu: boolean
    tab: Tab
    staticTraits?: IStaticTraits
    outdated: boolean
    newVersion?: string
    remoteLiveLogStreamEnabled: boolean
}

enum Tab {
    System,
    Channels,
    Drinks,
    Syrups,
    SyrupUsage,
    PumpUsage,
    Pouring,
    Catalog,
    Bluetooth,
    Wifi,
    GeneralSettings,
    AutoRinse,
    PublicTexts,
    UserSecurity,
    License,
    Sanitation,
    LiveLogs,
    CustomerData,
    Ota,
    FlashDevice
}

// Map tab enum to URL-friendly slugs
const TabSlug: { [key in Tab]: string } = {
    [Tab.System]: 'system',
    [Tab.Channels]: 'channels',
    [Tab.Drinks]: 'drinks',
    [Tab.Syrups]: 'syrups',
    [Tab.SyrupUsage]: 'syrup-usage',
    [Tab.PumpUsage]: 'pump-usage',
    [Tab.Pouring]: 'pouring',
    [Tab.Catalog]: 'catalog',
    [Tab.Bluetooth]: 'bluetooth',
    [Tab.Wifi]: 'wifi',
    [Tab.GeneralSettings]: 'settings',
    [Tab.AutoRinse]: 'auto-rinse',
    [Tab.PublicTexts]: 'public-texts',
    [Tab.UserSecurity]: 'user-security',
    [Tab.License]: 'license',
    [Tab.Sanitation]: 'sanitation',
    [Tab.LiveLogs]: 'live-logs',
    [Tab.CustomerData]: 'customer-data',
    [Tab.Ota]: 'ota',
    [Tab.FlashDevice]: 'flash-device',
}

// Reverse mapping: slug to Tab
const SlugToTab: { [key: string]: Tab } = {
    'system': Tab.System,
    'channels': Tab.Channels,
    'drinks': Tab.Drinks,
    'syrups': Tab.Syrups,
    'syrup-usage': Tab.SyrupUsage,
    'pump-usage': Tab.PumpUsage,
    'pouring': Tab.Pouring,
    'catalog': Tab.Catalog,
    'bluetooth': Tab.Bluetooth,
    'wifi': Tab.Wifi,
    'settings': Tab.GeneralSettings,
    'auto-rinse': Tab.AutoRinse,
    'public-texts': Tab.PublicTexts,
    'user-security': Tab.UserSecurity,
    'license': Tab.License,
    'sanitation': Tab.Sanitation,
    'live-logs': Tab.LiveLogs,
    'customer-data': Tab.CustomerData,
    'ota': Tab.Ota,
    'flash-device': Tab.FlashDevice,
}

export class Client extends Component<Props & RouteComponentProps> {
    state: State = {
        showMobileMenu: false,
        tab: Tab.System,
        outdated: false,
        newVersion: undefined,
        remoteLiveLogStreamEnabled: false,
    }

    machine?: IMachine

    listenerId?: IListenerUID

    componentDidMount = async () => {
        console.log('Loading 0x0005 Model Data (LTSP101)')

        const { client } = this.props

        // Read tab from URL
        const urlParams = new URLSearchParams(this.props.location.search)
        const tabSlug = urlParams.get('tab')
        if (tabSlug && SlugToTab[tabSlug] !== undefined) {
            this.setState({ tab: SlugToTab[tabSlug] })
        }

        const [clientResult, machineResult, staticTraitsResult] = await Promise.allSettled([
            getClientById(client.id),
            getMachine(client.id),
            getClientStaticTraits(client.id),
        ])

        if (clientResult.status === 'fulfilled') {
            const client = clientResult.value.data
            console.log('client', client)
            this.setState({ client })
        } else {
            console.error('Failed to get client data', clientResult.reason)
        }

        if (machineResult.status === 'fulfilled') {
            const machine = machineResult.value.data
            console.log('machine', machine)
            this.machine = machine
        } else {
            console.error('Failed to get machine data', machineResult.reason)
        }

        if (staticTraitsResult.status === 'fulfilled') {
            const staticTraits = staticTraitsResult.value.data
            console.log('staticTraits', staticTraits)
            this.setState({ staticTraits })

            try {
                const versionCheck = await isSoftwareOutdated(staticTraits.version, staticTraits.model)
                this.setState({
                    outdated: versionCheck.data.outdated,
                    newVersion: versionCheck.data.softwareVersion?.version,
                })
            } catch (e) {
                toast.error('Failed to check software version', e)
            }
        } else {
            console.error('Failed to get static traits', staticTraitsResult.reason)
        }

        try {
            const stream = await getRemoteLiveLogStream(client.id)
            const enabled = (stream.enabled ?? 0) === 1
            this.setState({ remoteLiveLogStreamEnabled: enabled })
        } catch (_) {
            this.setState({ remoteLiveLogStreamEnabled: false })
        }

        appRef.setHeader(
            <HiSwitchVertical className="me-2" size={28} />,
            () => `${this.machine?.name || getString('pagelabels.client')} ${client.id}`,
        )
        this.listenerId = onCustomerConnect(this.handleClientConnect.bind(this))
    }

    componentWillUnmount = () => {
        clearCustomerConnect(this.listenerId!)
    }

    handleClientConnect(clientConnect: IClientData, state: CustomerState) {
        // handled globally in App to keep disconnect behavior consistent
    }

    render() {
        return (
            <div className="flex flex-col lg:grid grid-cols-3 gap-5 lg:gap-10">
                <div>{this.renderSidebar()}</div>
                <div className="col-span-2">{this.renderContent()}</div>
            </div>
        )
    }

    renderSidebar() {
        return (
            <>
                <div className="hidden lg:block sticky top-5">
                    {this.renderSideNav()}
                    {this.renderStaticTraits()}
                </div>
                <div className="w-full flex lg:hidden justify-end">
                    <button
                        title="menu"
                        className="text-black dark:text-white hover:text-gray-300 focus:outline-none focus:text-gray-300"
                        onClick={() => {
                            this.setState({ showMobileMenu: !this.state.showMobileMenu })
                        }}
                    >
                        <HiOutlineMenu className="h-6 w-6" />
                    </button>
                </div>
                {this.state.showMobileMenu && (
                    <div className="mt-5">
                        {this.renderSideNav()}
                        {this.renderStaticTraits()}
                    </div>
                )}
            </>
        )
    }

    renderStaticTraits() {
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md">
                {this.renderStaticTrait(getString('static_traits.version'), HiChip, this.state.staticTraits?.version)}
                {this.renderStaticTrait(getString('static_traits.idf_ver'), HiChip, this.state.staticTraits?.idfVer)}
                {this.renderStaticTrait('Part Name', HiChip, this.state.staticTraits?.partName)}
                {this.renderStaticTrait('Compile Date', HiChip, this.state.staticTraits?.compileDate)}
                {this.renderStaticTrait('Compile Time', HiChip, this.state.staticTraits?.compileTime)}
                {this.renderStaticTrait(getString('static_traits.cores'), HiChip, this.state.staticTraits?.cores)}
                {this.renderStaticTrait(getString('static_traits.revision'), HiChip, this.state.staticTraits?.revision)}
                {this.renderStaticTrait('Flash Size', HiChip, this.state.staticTraits?.flashSize ? `${(this.state.staticTraits.flashSize / (1024 * 1024)).toFixed(2)} MB` : undefined)}
                {this.renderStaticTrait('WiFi MAC', HiChip, this.state.staticTraits?.wifiMac)}
                {this.renderStaticTrait('Bluetooth MAC', HiChip, this.state.staticTraits?.btMac)}
            </ul>
        )
    }

    renderStaticTrait(label: string, IconComp: React.ElementType, value?: string | number | JSX.Element) {
        return (
            <li className="flex items-center p-4 dark:text-white first:rounded-t-xl last:rounded-b-xl text-sm">
                <div className="flex items-center">
                    <IconComp className="me-5" />
                    {label}
                </div>
                <div className="flex-grow" />
                <div>{value}</div>
            </li>
        )
    }

    renderSideNav() {
        const { client } = this.props
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md mb-5">
                {this.renderSidebarItem(Tab.System, getString('system.title'))}
                {this.renderSidebarItem(Tab.Channels, getString('client.channels'))}
                {this.renderSidebarItem(Tab.Drinks, getString('drinks.title'))}
                {this.renderSidebarItem(Tab.Syrups, getString('syrups.title'))}
                {this.renderSidebarItem(Tab.SyrupUsage, getString('syrup_usage.title'))}
                {this.renderSidebarItem(Tab.PumpUsage, getString('pump_usage.title'))}
                {this.renderSidebarItem(Tab.Pouring, getString('pouring.title'))}
                {this.renderSidebarItem(Tab.Catalog, getString('ltsp101_catalog.title'))}
                {this.renderSidebarItem(Tab.Bluetooth, getString('bluetooth.title'))}
                {this.renderSidebarItem(Tab.Wifi, getString('wifi.title'))}
                {this.renderSidebarItem(Tab.GeneralSettings, getString('general_settings.title'))}
                {this.renderSidebarItem(Tab.LiveLogs, getString('general_settings.live_logs_title'))}
                {this.renderSidebarItem(Tab.AutoRinse, getString('auto_rinse.title'))}
                {this.renderSidebarItem(Tab.PublicTexts, getString('public_texts.title'))}
                {this.renderSidebarItem(Tab.UserSecurity, getString('system.user_security'))}
                {this.renderSidebarItem(Tab.License, getString('license.title'))}
                {this.renderSidebarItem(Tab.Sanitation, getString('sanitation.title'))}
                {this.renderSidebarItem(Tab.CustomerData, getString('client.customer_data'))}
                {this.renderSidebarItem(Tab.Ota, getString('client.ota'))}
                {client?.phyDebugger && this.renderSidebarItem(Tab.FlashDevice, getString('client.flash_device'))}
            </ul>
        )
    }

    renderSidebarItem(tabId: number, label: string) {
        const { tab } = this.state
        return (
            <li
                className={classnames(
                    tab === tabId && 'bg-gray-200 dark:bg-gray-700',
                    'flex items-center p-4 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors first:rounded-t-xl last:rounded-b-xl cursor-pointer',
                )}
                onClick={() => {
                    this.setState({ tab: tabId, showMobileMenu: false })
                    // Update URL with new tab
                    const tabSlug = TabSlug[tabId as Tab]
                    const newUrl = `${this.props.location.pathname}?tab=${tabSlug}`
                    this.props.history.replace(newUrl)
                }}
            >
                <HiHashtag className="me-5" />
                {label}
            </li>
        )
    }

    renderContent() {
        const { client } = this.props
        const { tab } = this.state
        if (!client) return undefined

        switch (tab) {
            case Tab.CustomerData:
                return <CustomerDataTab clientId={client.id} />
            case Tab.Channels:
                return <ChannelsTab clientId={client.id} />
            case Tab.Drinks:
                return <DrinksTab clientId={client.id} />
            case Tab.Syrups:
                return <SyrupsTab clientId={client.id} />
            case Tab.SyrupUsage:
                return <SyrupUsageTab clientId={client.id} />
            case Tab.PumpUsage:
                return <PumpUsageTab clientId={client.id} />
            case Tab.Pouring:
                return <PouringTab clientId={client.id} />
            case Tab.Catalog:
                return <CatalogTab clientId={client.id} />
            case Tab.System:
                return <SystemTab clientId={client.id} />
            case Tab.GeneralSettings:
                return <GeneralSettingsTab clientId={client.id} onRemoteLiveLogStreamChanged={this.handleRemoteLiveLogStreamChanged} />
            case Tab.LiveLogs:
                return <LiveLogsTab clientId={client.id} />
            case Tab.AutoRinse:
                return <AutoRinseTab clientId={client.id} />
            case Tab.PublicTexts:
                return <PublicTextsTab clientId={client.id} />
            case Tab.UserSecurity:
                return <UserSecurityTab clientId={client.id} />
            case Tab.License:
                return <LicenseTab clientId={client.id} />
            case Tab.Sanitation:
                return <SanitationTab clientId={client.id} />
            case Tab.Bluetooth:
                return <BluetoothTab clientId={client.id} />
            case Tab.Wifi:
                return <WifiTab clientId={client.id} />
            case Tab.Ota:
                return <OtaTab clientId={client.id} />
            case Tab.FlashDevice:
                return <FlashDeviceTab clientId={client.id} />
        }
    }

    handleRemoteLiveLogStreamChanged = (enabled: boolean) => {
        this.setState({ remoteLiveLogStreamEnabled: enabled })
    }
}
